import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import DOMPurify from "dompurify";
import {
  LuMapPin,
  LuClock,
  LuBuilding2,
  LuDollarSign,
  LuGraduationCap,
  LuSparkles,
  LuZap,
  LuExternalLink,
  LuInfo,
  LuRefreshCw,
  LuArrowLeft,
  LuBookmark,
  LuBookmarkCheck,
} from "react-icons/lu";
import { Btn, Card, Badge, Skel, Note } from "../components/Ui";
import { setdetailedJob } from "../../store/actions/actions";
import { jobFacts } from "../matchScore";
import {
  ResumeInsightsSheet,
  AutoApplySheet,
  EasyApplySheet,
  useSavedJobs,
  FormattedDescription,
  WhyYouMatch,
} from "./JobSearch";
import "./jobs.css";

const JOB_DETAIL_API = "https://us-east1-foursssolutions.cloudfunctions.net/Get_Jobs_with_job_id_v2";

function getDaysAgo(postedDate) {
  const posted = new Date(postedDate);
  const today = new Date();
  posted.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  const differenceInDays = Math.floor((today - posted) / (1000 * 60 * 60 * 24));
  if (differenceInDays <= 0) return "Today";
  return `${differenceInDays} day${differenceInDays > 1 ? "s" : ""} ago`;
}

function companyInitials(name) {
  return (name || "?").substring(0, 2).toUpperCase();
}

/* mint-dot bullets (Lovable job-detail lists) */
function DotList({ items, dim }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <ul className="dot-list">
      {items.map((item, index) => (
        <li key={index}>
          <span className={`dot${dim ? " dim" : ""}`} />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export default function JobDetail() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { apiToken } = useSelector((s) => s.profile);

  const [jobId, setJobId] = useState("");
  const [detailedData, setDetailedData] = useState(null);
  const [similarData, setSimilarData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [insightsOpen, setInsightsOpen] = useState(false);
  const [autoApplyOpen, setAutoApplyOpen] = useState(false);
  const [easyApplyOpen, setEasyApplyOpen] = useState(false);

  const { savedIds, toggleSaved } = useSavedJobs();

  /* legacy jobDetailed showSecondary behavior — a fixed action bar
     appears once the page scrolls past 20px (passive listener,
     initial call on mount, removed on unmount) */
  const [showSecondary, setShowSecondary] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY || document.documentElement.scrollTop || 0;
      setShowSecondary(y > 20);
    };
    onScroll(); // set initial state on mount
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* extract jobId from ?id= — legacy restores '+' that URL decoding
     turned into spaces */
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const rawToken = queryParams.get("id");
    const tokenFromUrl = rawToken?.replace(/ /g, "+");
    if (tokenFromUrl) setJobId(tokenFromUrl);
  }, [location.search]);

  const fetchJobDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(JOB_DETAIL_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify({ job_id: jobId }),
      });
      const data = await response.json();
      if (response.ok) {
        setDetailedData(data.job_details);
        setSimilarData(data.similar_jobs);
        dispatch(setdetailedJob(""));
      } else {
        console.error("Failed to fetch job details:", data);
        setError("We could not load this job. It may have been removed.");
      }
    } catch (err) {
      console.error("Error fetching job details:", err);
      // Show a human message instead of the raw "Failed to fetch" (Pavithra Dashboard #2)
      setError("We couldn't load this job right now. It may have been removed or is no longer available.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (jobId) {
      fetchJobDetails();
      dispatch(setdetailedJob(jobId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  /* ------- overview normalization (legacy logic) ------- */
  /* ATS + JSearch jobs store job_description as a plain STRING; only internal
     employer-posted jobs use the {overview, additional_comments} object shape */
  const jdRaw = detailedData?.job_description;
  const isPlainDescription = typeof jdRaw === "string" && jdRaw.trim().length > 0;
  const overviewRaw = isPlainDescription ? null : jdRaw?.overview;
  const overviewHtmlRaw = Array.isArray(overviewRaw) ? overviewRaw[0] || "" : overviewRaw || "";
  const sanitizedOverviewHtml = DOMPurify.sanitize(overviewHtmlRaw.replace(/\.,\s*/g, ". "));

  const additionalRaw = isPlainDescription ? null : jdRaw?.additional_comments;
  const additionalHtmlRaw = Array.isArray(additionalRaw) ? additionalRaw[0] || "" : additionalRaw || "";
  const sanitizedAdditionalHtml = DOMPurify.sanitize(additionalHtmlRaw);
  /* legacy renders Additional comments only when the raw field's own
     length (array length for arrays, string length otherwise) is > 1 */
  const showAdditionalComments = (additionalRaw?.length ?? 0) > 1;

  const cleanedItems = useMemo(() => {
    const items = Array.isArray(overviewRaw)
      ? overviewRaw
      : typeof overviewRaw === "string"
      ? overviewRaw.includes("\n")
        ? overviewRaw.split(/\r?\n/)
        : overviewRaw.split(/(?<=[.!?])\s+(?=[A-Z])/)
      : [];
    return items.map((item) => String(item).trim()).filter(Boolean);
  }, [overviewRaw]);

  const facts = [
    {
      icon: <LuDollarSign size={17} />,
      tone: "green",
      label: "Salary range",
      value: `${detailedData?.salary_range || "Not specified"} ${
        detailedData?.salary_type === "Hourly" ? "/ Hour" : "/ Year"
      }`,
    },
    {
      icon: <LuGraduationCap size={17} />,
      tone: "blue",
      label: "Experience",
      // Fall back to the JD-derived years (same source the list uses) so the full
      // page matches the list instead of showing "Not specified" (Pavithra Jobs #4).
      value:
        detailedData?.experience_range ||
        (jobFacts(detailedData)?.yearsRequired ? `${jobFacts(detailedData).yearsRequired}+ yrs` : "Not specified"),
    },
    {
      icon: <LuBuilding2 size={17} />,
      tone: "purple",
      label: "Employment type",
      value: `${detailedData?.job_employment_type || "Not specified"}`,
    },
    {
      icon: <LuClock size={17} />,
      tone: "orange",
      label: "Posted",
      value: `${
        detailedData?.job_posted_at_datetime_utc ? getDaysAgo(detailedData.job_posted_at_datetime_utc) : "N/A"
      }`,
    },
  ];

  const responsibilities =
    detailedData?.job_highlights?.Responsibilities?.length > 0
      ? detailedData.job_highlights.Responsibilities
      : detailedData?.job_highlights?.responsibilities?.length > 0
      ? detailedData.job_highlights.responsibilities
      : null;

  const requirements =
    detailedData?.job_highlights?.requirements?.length > 0 ? detailedData.job_highlights.requirements : null;

  const qualifications = (() => {
    const q = detailedData?.job_highlights?.Qualifications;
    if (!Array.isArray(q) || q.length === 0) return null;
    // backend stuffs the whole description into Qualifications as a fallback —
    // don't render the same text twice on plain-description jobs
    if (q.length === 1 && isPlainDescription && String(q[0]).trim() === jdRaw.trim()) return null;
    return q;
  })();

  const benefits =
    Array.isArray(detailedData?.job_highlights?.benefits) &&
    detailedData.job_highlights.benefits[0]?.trim().length > 0
      ? detailedData.job_highlights.benefits.filter((b) => b.trim().length > 0)
      : null;

  const savedId = detailedData?.job_id || jobId;
  const saved = savedIds.includes(savedId);

  const hasBodySections =
    isPlainDescription ||
    cleanedItems.length > 0 ||
    responsibilities ||
    requirements ||
    qualifications ||
    benefits ||
    showAdditionalComments;

  if (loading) {
    return (
      <div className="detail-col">
        <div>
          <Link to="/search-job" className="back-link">
            <LuArrowLeft size={15} /> Back to matches
          </Link>
        </div>
        <Card pad style={{ borderRadius: 24 }}>
          <div className="job-top">
            <Skel w={56} h={56} r={14} />
            <div className="grow stack-2">
              <Skel w="60%" h={22} />
              <Skel w="40%" h={14} />
            </div>
          </div>
        </Card>
        <div className="job-facts">
          {[...Array(4)].map((_, i) => (
            <Skel key={i} h={68} r={18} />
          ))}
        </div>
        <Card pad style={{ borderRadius: 24 }}>
          <Skel h={14} />
          <Skel h={14} w="90%" style={{ marginTop: 8 }} />
          <Skel h={14} w="75%" style={{ marginTop: 8 }} />
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="detail-col">
        <div>
          <Link to="/search-job" className="back-link">
            <LuArrowLeft size={15} /> Back to matches
          </Link>
        </div>
        <Note tone="danger">
          <LuInfo size={16} />
          <span className="grow">{error}</span>
          <Btn size="sm" variant="ghost" onClick={fetchJobDetails}>
            <LuRefreshCw size={14} /> Retry
          </Btn>
        </Note>
      </div>
    );
  }

  return (
    <div className="detail-col">
      <div>
        <Link to="/search-job" className="back-link">
          <LuArrowLeft size={15} /> Back to matches
        </Link>
      </div>

      {/* ------- header card ------- */}
      <Card pad style={{ borderRadius: 24 }}>
        <div className="job-top">
          <span className="job-logo-lg">{companyInitials(detailedData?.employer_name)}</span>
          <div className="grow" style={{ minWidth: 0 }}>
            <h1 className="t-h1">{detailedData?.job_title}</h1>
            <div className="meta-row t-sm mt-1">
              <span>{detailedData?.employer_name}</span>
              <span className="meta-dot">
                <LuMapPin size={12} /> {detailedData?.job_city}, {detailedData?.job_state},{" "}
                {detailedData?.job_country}
              </span>
              <span className="meta-dot">{detailedData?.job_is_remote === false ? "Onsite" : "Remote"}</span>
            </div>
            {detailedData?.job_status === "Closed" ? (
              <div className="mt-1">
                <Badge tone="danger">Position closed</Badge>
              </div>
            ) : null}
          </div>
        </div>
        <div className="detail-actions mt-3">
          {detailedData?.job_source === "Internal" ? (
            <button type="button" className="btn btn-pill btn-mint" onClick={() => setEasyApplyOpen(true)}>
              <LuZap size={15} /> Easy Apply
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-pill btn-mint"
              onClick={() => window.open(detailedData?.job_apply_link, "_blank")}
            >
              <LuExternalLink size={15} /> Apply now
            </button>
          )}
          <Btn variant="ghost" className="btn-pill" onClick={() => setInsightsOpen(true)}>
            <LuSparkles size={15} /> Analyze match
          </Btn>
          <Btn variant="quiet" className="btn-pill" onClick={() => toggleSaved(savedId)}>
            {saved ? <LuBookmarkCheck size={15} className="t-accent" /> : <LuBookmark size={15} />}
            {saved ? "Saved" : "Save"}
          </Btn>
          <Btn variant="quiet" className="btn-pill" onClick={() => setAutoApplyOpen(true)}>
            <LuZap size={15} /> Auto Apply
          </Btn>
          <Btn variant="quiet" className="btn-pill" onClick={() => navigate("/job-match")}>
            <LuSparkles size={15} /> Match a job
          </Btn>
        </div>
      </Card>

      {/* ------- quick facts (glass stat tiles) ------- */}
      <div className="job-facts">
        {facts.map((f, i) => (
          <div className="fact" key={i}>
            <span className={`fact-ic ${f.tone}`}>{f.icon}</span>
            <span>
              <span className="fact-l">{f.label}</span>
              <br />
              <span className="fact-v">{f.value}</span>
            </span>
          </div>
        ))}
      </div>

      <WhyYouMatch
        job={detailedData}
        skills={(() => {
          try {
            const arr = JSON.parse(sessionStorage.getItem("cs_user_skills") || "[]");
            return Array.isArray(arr) ? arr : [];
          } catch {
            return [];
          }
        })()}
      />

      {/* ------- description sections (legacy field mapping) ------- */}
      {hasBodySections ? (
        <Card pad style={{ borderRadius: 24 }}>
          <div className="stack-4">
            {isPlainDescription ? (
              <section>
                <FormattedDescription text={jdRaw} leadHeading="About the role" />
              </section>
            ) : null}
            {!isPlainDescription && cleanedItems.length === 1 ? (
              <section>
                <h2 className="t-h3 mb-2">About the role</h2>
                {/* eslint-disable-next-line react/no-danger */}
                <div className="prose" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(cleanedItems[0]) }} />
              </section>
            ) : cleanedItems.length > 1 ? (
              <section>
                <h2 className="t-h3 mb-2">Overview</h2>
                {/* eslint-disable-next-line react/no-danger */}
                <div className="prose" dangerouslySetInnerHTML={{ __html: sanitizedOverviewHtml }} />
              </section>
            ) : null}

            {responsibilities || requirements ? (
              <div className="role-cols">
                {responsibilities ? (
                  <section>
                    <h2 className="t-h3 mb-2">What you'll do</h2>
                    <DotList items={responsibilities} />
                  </section>
                ) : null}
                {requirements ? (
                  <section>
                    <h2 className="t-h3 mb-2">What they're looking for</h2>
                    <DotList items={requirements} dim />
                  </section>
                ) : null}
              </div>
            ) : null}

            {qualifications ? (
              <section>
                <h2 className="t-h3 mb-2">Qualifications</h2>
                <DotList items={qualifications} dim />
              </section>
            ) : null}

            {benefits ? (
              <div className="bene-panel">
                <h2 className="t-h3 row" style={{ gap: 8 }}>
                  <LuBuilding2 size={16} /> Benefits at {detailedData?.employer_name}
                </h2>
                <div className="mt-2">
                  <DotList items={benefits} />
                </div>
              </div>
            ) : null}

            {showAdditionalComments ? (
              <section>
                <h2 className="t-h3 mb-2">Additional comments</h2>
                {/* eslint-disable-next-line react/no-danger */}
                <div className="prose" dangerouslySetInnerHTML={{ __html: sanitizedAdditionalHtml }} />
              </section>
            ) : null}
          </div>
        </Card>
      ) : null}

      {/* ------- similar matches (Lovable card grid) ------- */}
      {similarData?.length > 0 ? (
        <section>
          <h2 className="t-h2">Similar matches</h2>
          <div className="sim-grid mt-2">
            {similarData.map((item) => (
              <Card
                hover
                key={item.job_id}
                className="sim-card"
                style={{ borderRadius: 18 }}
                onClick={() => navigate(`/job/detailed?id=${item.job_id}`)}
              >
                <div className="job-top">
                  <span className="job-logo">{companyInitials(item.employer_name)}</span>
                  <div className="grow" style={{ minWidth: 0 }}>
                    <div className="job-card-title trunc">{item.job_title}</div>
                    <div className="t-xs trunc">{item.employer_name}</div>
                  </div>
                </div>
                <div className="job-card-meta">
                  <span className="row" style={{ gap: 4 }}>
                    <LuMapPin size={12} /> {item?.job_city}, {item?.job_state}
                  </span>
                  <span className="row" style={{ gap: 4 }}>
                    <LuClock size={12} />{" "}
                    {item?.job_posted_at_datetime_utc ? getDaysAgo(item.job_posted_at_datetime_utc) : "N/A"}
                  </span>
                </div>
              </Card>
            ))}
          </div>
        </section>
      ) : null}

      {/* ------- sticky secondary actions (legacy showSecondary bar) ------- */}
      {showSecondary && detailedData ? (
        <div className="sticky-bar" aria-label="Secondary job actions">
          <div className="sticky-id">
            <span className="job-logo">{companyInitials(detailedData?.employer_name)}</span>
            <div className="grow" style={{ minWidth: 0 }}>
              <div className="sticky-title trunc">{detailedData?.job_title}</div>
              <div className="sticky-sub trunc">
                {detailedData?.employer_name} · {detailedData?.job_city}, {detailedData?.job_state},{" "}
                {detailedData?.job_country} · {detailedData?.job_is_remote === false ? "Onsite" : "Remote"}
              </div>
            </div>
          </div>
          {detailedData?.job_source === "Internal" ? (
            <button type="button" className="btn btn-pill btn-mint" onClick={() => setEasyApplyOpen(true)}>
              <LuZap size={15} /> Easy Apply
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-pill btn-mint"
              onClick={() => window.open(detailedData?.job_apply_link, "_blank")}
            >
              <LuExternalLink size={15} /> Apply now
            </button>
          )}
        </div>
      ) : null}

      {/* ------- overlays (shared with the search page) ------- */}
      <ResumeInsightsSheet open={insightsOpen} onClose={() => setInsightsOpen(false)} job={detailedData} />
      <AutoApplySheet open={autoApplyOpen} onClose={() => setAutoApplyOpen(false)} />
      <EasyApplySheet open={easyApplyOpen} onClose={() => setEasyApplyOpen(false)} job={detailedData} />
    </div>
  );
}
